import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useCallback, useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { type InferType, object, string } from 'yup';

import { useTrackContextValueAdded } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Field, FieldLabel, FieldText } from '@air/primitive-field';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';
import { Textarea } from '@air/primitive-textarea';
import { useToasts } from '@air/provider-toast';
import { type Clip } from '@air/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { usePrivateContextSourcesAddValueMutation } from '~/components/v2/hooks/queries/mutations/usePrivateContextSourcesAddValueMutation';
import { usePrivateContextSourcesGet } from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';
import { usePrivateContextSourcesList } from '~/components/v2/hooks/queries/usePrivateContextSourcesList';
import { isContextSourceFieldFile } from '~/components/v2/utils/context-sources/isContextSourceFieldFile';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export const PrivateContextSourcesAddClipModalFormValidationSchema = object({
  fieldId: string().required('Field is required').default(''),
  instruction: string().optional().default(''),
  label: string().optional().default(''),
});

export type PrivateContextSourcesAddClipModalFormInput = InferType<
  typeof PrivateContextSourcesAddClipModalFormValidationSchema
>;

export type PrivateContextSourcesAddClipModalProps = {
  clip: Clip;
};

export const PrivateContextSourcesAddClipModal = memo(
  ({ clip, onClose }: AirModalProps<PrivateContextSourcesAddClipModalProps>) => {
    const { showToast } = useToasts();
    const { trackContextValueAdded } = useTrackContextValueAdded();
    const formId = useId();
    const { currentWorkspace } = useCurrentWorkspace();
    const {
      control,
      formState: { errors },
      handleSubmit,
      register,
    } = useForm<PrivateContextSourcesAddClipModalFormInput>({
      defaultValues: {
        fieldId: '',
        instruction: '',
        label: '',
      },
      mode: 'all',
      resolver: yupResolver(PrivateContextSourcesAddClipModalFormValidationSchema),
    });
    const { data: contextSources } = usePrivateContextSourcesList({
      workspaceId: currentWorkspace?.id,
    });
    const { mutate: addValue, isPending: isAddingValue } = usePrivateContextSourcesAddValueMutation();

    const { data: contextSource, isLoading: isLoadingContextSource } = usePrivateContextSourcesGet(
      {
        contextSourceId: contextSources?.data?.[0]?.id,
        workspaceId: currentWorkspace?.id,
      },
      {
        enabled: !!contextSources?.data?.[0]?.id,
      },
    );

    const handleAddFile = useCallback(
      (values: PrivateContextSourcesAddClipModalFormInput) => {
        if (values.fieldId && contextSource?.id && currentWorkspace?.id) {
          addValue(
            {
              contextSourceId: contextSource.id,
              workspaceId: currentWorkspace.id,
              fieldId: values.fieldId,
              instruction: values.instruction,
              label: values.label,
              assetId: clip.assetId,
              versionId: clip.id,
            },
            {
              onError: (error) => {
                showToast('Failed to add file', {
                  color: 'red',
                });

                reportErrorToBugsnag({
                  error,
                  context: 'Failed to add file',
                });

                onClose?.();
              },
              onSuccess: () => {
                const selectedField = contextSource?.fields.find((f) => f.id === values.fieldId);
                trackContextValueAdded({
                  source: 'manual',
                  value_type: 'file',
                  field_name: selectedField?.name ?? '',
                  label: !!values.label,
                  ai_instruction: !!values.instruction,
                });
                showToast('File added successfully', {
                  color: 'black',
                });

                onClose?.();
              },
            },
          );
        }
      },
      [
        addValue,
        clip.assetId,
        clip.id,
        contextSource?.fields,
        contextSource?.id,
        currentWorkspace?.id,
        onClose,
        showToast,
        trackContextValueAdded,
      ],
    );

    const fields = useMemo(() => {
      return contextSource?.fields ?? [];
    }, [contextSource?.fields]);

    const filteredFields = useMemo(() => {
      // @TODO AI-906: [Show font fields in add to context modal](https://linear.app/air/issue/AI-906/show-font-fields-in-add-to-context-modal)
      // const isFont = FONT_EXTENSIONS.includes(clip.ext.toLowerCase());

      // if (isFont) {
      //   return fields.filter(isContextSourceFieldFont);
      // }

      return fields.filter(isContextSourceFieldFile);
    }, [fields]);

    return (
      <Modal className="flex flex-col gap-6" dangerouslyBypassFocusLock isOpen onDismiss={onClose}>
        <header className="flex items-center justify-between">
          <ModalTitle>Add file</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </header>

        <ModalDescription>Add a file to your context source.</ModalDescription>

        <form className="flex flex-col gap-6" id={formId} onSubmit={handleSubmit(handleAddFile)}>
          <Field>
            <FieldLabel htmlFor={`${formId}-field`}>Field</FieldLabel>
            <Controller
              control={control}
              name="fieldId"
              render={({ field }) =>
                isLoadingContextSource ? (
                  <Select disabled value="">
                    <SelectTrigger disabled id={`${formId}-field`} placeholder="Choose a field..." size="large" />
                  </Select>
                ) : (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger placeholder="Choose a field..." size="large" />
                    <SelectContent>
                      {filteredFields.map((field) => (
                        <SelectItem key={field.id} value={field.id}>
                          {field.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            />
            {errors.fieldId && <FieldText color="red">{errors.fieldId.message}</FieldText>}
          </Field>

          <Field>
            <FieldLabel htmlFor={`${formId}-label`}>Label</FieldLabel>
            <Input
              autoComplete="off"
              data-1p-ignore
              hasError={!!errors.label}
              id={`${formId}-label`}
              placeholder="e.g., Intro clip, B-roll"
              size="large"
              {...register('label')}
            />
            {errors.label && <FieldText color="red">{errors.label.message}</FieldText>}
          </Field>
          <Field>
            <FieldLabel htmlFor={`${formId}-instructions`}>Instructions</FieldLabel>
            <Textarea
              autoGrow
              className="min-h-28"
              id={`${formId}-instructions`}
              placeholder="Instructions for this clip"
              size="large"
              {...register('instruction')}
            />
            {errors.instruction && <FieldText color="red">{errors.instruction.message}</FieldText>}
          </Field>
        </form>

        <footer className="flex justify-end gap-2">
          <Button appearance="ghost" color="grey" onClick={onClose} size="large">
            Cancel
          </Button>
          <Button appearance="filled" color="blue" isLoading={isAddingValue} form={formId} size="large" type="submit">
            Add
          </Button>
        </footer>
      </Modal>
    );
  },
);

PrivateContextSourcesAddClipModal.displayName = 'PrivateContextSourcesAddClipModal';
